import { useState, useMemo } from 'react';
import { useStore } from '../../../hooks/useStore';
import { useAuth } from '../../../hooks/useAuth';
import {
  getContractsByHtx, getFarmersByHtx, getSeasonsByHtx, getHarvestsByHtx, getRiskProfileByFarmer,
  getFarmerKyc, getLimitsByFarmer, getLoansByHtx,
  getEditHistories,
} from '../../../store';
import { EmptyState } from '../../../components/base/EmptyState';
import ContractStats from './components/ContractStats';
import ContractFilters from './components/ContractFilters';
import ContractCard from './components/ContractCard';
import ContractDetailModal from './components/ContractDetailModal';
import { CreateContractModal } from './components/CreateContractModal';
import { EditContractModal } from './components/EditContractModal';
import type { Contract } from '../../../types';

/** HTX_STAFF can now create a Contract (draft) for its own farmers, in addition to
 * ADMIN/OPS/CO — see contract.controller.ts's @Roles on POST/PATCH /contracts. */
export default function HtxContractPage() {
  useStore();
  const { user } = useAuth();
  const contracts = getContractsByHtx(user?.htxId);
  const farmers = getFarmersByHtx(user?.htxId);
  const allSeasons = getSeasonsByHtx(user?.htxId);
  const allHarvests = getHarvestsByHtx(user?.htxId);
  const allLoans = getLoansByHtx(user?.htxId);
  const allEditHistories = getEditHistories();

  const [filtered, setFiltered] = useState<Contract[]>(contracts);
  const [detailItem, setDetailItem] = useState<Contract | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<Contract | null>(null);

  const farmerOptions = useMemo(() => farmers.map((f) => ({ id: f.id, name: f.name })), [farmers]);
  const seasonOptions = useMemo(() => allSeasons.map((s) => ({ id: s.id, name: s.name })), [allSeasons]);

  const farmerKycMap = useMemo(() => {
    const map: Record<string, ReturnType<typeof getFarmerKyc>> = {};
    farmers.forEach((f) => {
      map[f.id] = getFarmerKyc(f);
    });
    return map;
  }, [farmers]);

  const getFarmerName = (id: string) => farmers.find((f) => f.id === id)?.name || id;
  const getSeasonNames = (ids?: string[]) => (ids || []).map((sid) => allSeasons.find((s) => s.id === sid)?.name || '').filter(Boolean);
  const getCayTrong = (ids?: string[]) => {
    const s = allSeasons.find((se) => ids?.includes(se.id));
    return s?.cayTrong || '';
  };

  const openDetail = (item: Contract) => {
    setDetailItem(item);
  };

  const activeList = filtered.filter((c) => !c.isDeleted);

  // Detail data computed when detailItem changes
  const detailData = detailItem ? (() => {
    const riskProfile = getRiskProfileByFarmer(detailItem.farmerId);
    const contractLoans = allLoans.filter((l) =>
      l.loai === 'Tiền' &&
      l.seasonId &&
      detailItem.seasonIds?.includes(l.seasonId)
    );
    const contractEditHistories = allEditHistories.filter(
      (eh) => eh.entityType === 'Contract' && eh.entityId === detailItem.id
    );
    return { riskProfile, contractLoans, contractEditHistories };
  })() : null;

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* ── Main content area ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="mobile-page md:px-6 md:py-5">

          {/* ── Header ── */}
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-slate-900">Hợp đồng</h2>
              <p className="metric-caption mt-0.5 text-[11px] text-slate-400">
                Danh sách hợp đồng của HTX — có thể tạo hợp đồng nháp mới cho nông hộ của mình.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="shrink-0 px-3 py-2 rounded-lg text-xs font-semibold text-white bg-agrida-700 hover:bg-agrida-800 transition-colors whitespace-nowrap"
            >
              + Tạo hợp đồng
            </button>
          </div>

          {/* ── Stats ── */}
          <div className="mb-4">
            <ContractStats />
          </div>

          {/* ── Filters ── */}
          <div className="mb-4">
            <ContractFilters contracts={contracts} farmers={farmerOptions} seasons={seasonOptions} onFilterChange={setFiltered} />
          </div>

          {/* ── List header ── */}
          <div className="mb-2.5 flex items-center justify-between"><h3 className="text-sm font-semibold text-slate-900">Danh sách hợp đồng</h3><span className="text-[11px] text-slate-400">{activeList.length} hợp đồng</span></div>

          {/* ── Contract list ── */}
          {activeList.length === 0 ? (
            <EmptyState
              icon="ri-draft-line"
              title="Chưa có hợp đồng"
              description="Chưa có hợp đồng nào cho HTX này."
            />
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
              {activeList.map((c) => (
                <ContractCard
                  key={c.id}
                  contract={c}
                  farmerName={getFarmerName(c.farmerId)}
                  farmerId={c.farmerId}
                  farmerKyc={farmerKycMap[c.farmerId]}
                  seasonNames={getSeasonNames(c.seasonIds)}
                  cayTrong={getCayTrong(c.seasonIds)}
                  onViewDetail={() => openDetail(c)}
                />
              ))}
            </div>
          )}

          {/* ── Bottom spacer ── */}
          <div className="h-8" />
        </div>
      </div>

      {/* ── MODALS ── */}

      {/* Detail modal — popup on all devices */}
      {detailItem && detailData && (
        <ContractDetailModal
          contract={detailItem}
          farmerName={getFarmerName(detailItem.farmerId)}
          farmerId={detailItem.farmerId}
          farmerKyc={farmerKycMap[detailItem.farmerId] || { kycStatus: 'ekyc_verified', creditScore: 600, riskLevel: 'Cần chú ý' }}
          seasonNames={getSeasonNames(detailItem.seasonIds)}
          seasons={allSeasons}
          relatedHarvests={allHarvests.filter((h) => h.seasonId && detailItem.seasonIds?.includes(h.seasonId))}
          relatedLimits={getLimitsByFarmer(detailItem.farmerId)}
          relatedLoans={detailData.contractLoans}
          editHistories={detailData.contractEditHistories}
          riskMucDo={detailData.riskProfile?.mucDo}
          riskMoTa={detailData.riskProfile?.moTa}
          onClose={() => setDetailItem(null)}
          onEdit={() => setEditItem(detailItem)}
        />
      )}

      {editItem && (
        <EditContractModal
          contract={editItem}
          htxId={user?.htxId}
          onClose={() => setEditItem(null)}
          onUpdated={(updated) => {
            setEditItem(null);
            setDetailItem(updated);
          }}
        />
      )}

      {createOpen && user?.htxId && (
        <CreateContractModal
          htxId={user.htxId}
          // Only confirmed members — a farmer still "Chờ xác nhận" or "Từ chối"
          // isn't manageable yet (backend's own assertActiveMember refuses this
          // at submit time; Feedback 08 A3 found the picker let you fill out the
          // whole form for one anyway before hitting that 403/400).
          farmers={farmers.filter((f) => f.membershipStatus === 'Đã xác nhận' || !f.membershipStatus)}
          onClose={() => setCreateOpen(false)}
          onCreated={() => setCreateOpen(false)}
        />
      )}
    </div>
  );
}
